import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { apiBaseUrl } from '../../util/api';

const PROFILE_IMAGE_VARIANTS = [
  'variants.default',
  'variants.square-xsmall',
  'variants.square-xsmall2x',
  'variants.square-small',
  'variants.square-small2x',
];

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage, supabaseImageUrl }
      const { id, uploadedImage, supabaseImageUrl } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage, supabaseImageUrl };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return async (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': PROFILE_IMAGE_VARIANTS,
    };

    try {
      // Upload to Sharetribe (for backward compatibility)
      const resp = await sdk.images.upload(bodyParams, queryParams);
      const uploadedImage = resp.data.data;

      // Also upload to Supabase profile_pics bucket
      let supabaseImageUrl = null;
      try {
        const formData = new FormData();
        formData.append('image', actionPayload.file);

        // Get API base URL
        const baseUrl = apiBaseUrl();
        const apiUrl = `${baseUrl}/api/supabase/profile-pics/upload`;

        const supabaseResponse = await fetch(apiUrl, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (supabaseResponse.ok) {
          const supabaseData = await supabaseResponse.json();
          if (supabaseData.success && supabaseData.url) {
            supabaseImageUrl = supabaseData.url;
          }
        } else {
          console.warn(
            'Failed to upload profile picture to Supabase, continuing with Sharetribe only'
          );
        }
      } catch (supabaseError) {
        console.warn(
          'Error uploading to Supabase, continuing with Sharetribe only:',
          supabaseError
        );
        // Continue even if Supabase upload fails
      }

      // Store both Sharetribe image and Supabase URL
      dispatch(
        uploadImageSuccess({
          data: {
            id,
            uploadedImage,
            supabaseImageUrl, // Add Supabase URL to the response
          },
        })
      );

      // Return the upload result so callers can read the Sharetribe image id
      // and the Supabase URL (e.g. ProfileSetupPage onboarding flow).
      return { id, uploadedImage, supabaseImageUrl };
    } catch (e) {
      dispatch(uploadImageError({ id, error: storableError(e) }));
      return null;
    }
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': PROFILE_IMAGE_VARIANTS,
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => {
        dispatch(updateProfileError(storableError(e)));
        throw e;
      });
  };
};
